import ChoirSong from "../DTOs/ChoirSong";

// Pure helpers for the repertoire view (GOD-18). All dates are YYYY-MM-DD
// strings, which compare correctly as text.

export function toIsoDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** The day `months` months before `today`. */
export function monthsBefore(today: string, months: number): string {
    const date = new Date(`${today}T00:00:00`);
    date.setMonth(date.getMonth() - months);
    return toIsoDate(date);
}

export type SongUsage = {
    /** Last performance up to today; null if never sung (or not known yet). */
    lastSung: string | null;
    /** Performances in the twelve months up to today. */
    lastYearCount: number;
    /** Next planned performance after today, if any. */
    nextPlanned: string | null;
};

export function usageOf(song: Pick<ChoirSong, "performanceDates">, today: string): SongUsage {
    const dates = song.performanceDates ?? [];
    const past = dates.filter((date) => date <= today);
    const since = monthsBefore(today, 12);
    return {
        lastSung: past.length > 0 ? past[past.length - 1] : null,
        lastYearCount: past.filter((date) => date > since).length,
        nextPlanned: dates.find((date) => date > today) ?? null,
    };
}

/** Not sung for more than a year – or never. */
export function isLongUnsung(song: Pick<ChoirSong, "performanceDates">, today: string): boolean {
    const { lastSung } = usageOf(song, today);
    return lastSung === null || lastSung <= monthsBefore(today, 12);
}

export type Besetzung = "chor" | "solo" | "orchester";
export type RepertoireFilter = {
    search: string;
    besetzung: Besetzung | "";
    tag: string;
    longUnsung: boolean;
    unverifiedOnly: boolean;
    showArchived: boolean;
};

export const EMPTY_FILTER: RepertoireFilter = {
    search: "",
    besetzung: "",
    tag: "",
    longUnsung: false,
    unverifiedOnly: false,
    showArchived: false,
};

export function filterSongs(songs: ChoirSong[], filter: RepertoireFilter, today: string): ChoirSong[] {
    const term = filter.search.trim().toLowerCase();
    return songs.filter((song) => {
        if (song.archived && !filter.showArchived) return false;
        if (filter.unverifiedOnly && !song.unverified) return false;
        if (filter.besetzung && !song.executionType?.[filter.besetzung]) return false;
        if (filter.tag && !song.tags.includes(filter.tag)) return false;
        if (filter.longUnsung && !isLongUnsung(song, today)) return false;
        if (!term) return true;
        return [song.name.toString(), song.songbookNumber, ...song.tags].some((text) => text.toLowerCase().includes(term));
    });
}

export type RepertoireSort = "name" | "lastSung" | "count" | "songbook";

export function sortSongs(songs: ChoirSong[], sort: RepertoireSort, descending: boolean, today: string): ChoirSong[] {
    const byName = (a: ChoirSong, b: ChoirSong) => a.name.toString().localeCompare(b.name.toString(), "de");
    const compare = (a: ChoirSong, b: ChoirSong): number => {
        if (sort === "lastSung") return (usageOf(a, today).lastSung ?? "").localeCompare(usageOf(b, today).lastSung ?? "");
        if (sort === "count") return usageOf(a, today).lastYearCount - usageOf(b, today).lastYearCount;
        if (sort === "songbook") {
            return a.songbookNumber.localeCompare(b.songbookNumber, "de", { numeric: true });
        }
        return byName(a, b);
    };
    return [...songs].sort((a, b) => (descending ? -compare(a, b) : compare(a, b)) || byName(a, b));
}

/** All tags in use, sorted. */
export function tagsOf(songs: ChoirSong[]): string[] {
    return Array.from(new Set(songs.flatMap((song) => song.tags))).sort((a, b) => a.localeCompare(b, "de"));
}

/** "Advent, Ostern ,advent" -> ["Advent", "Ostern"] */
export function parseTags(input: string): string[] {
    const tags: string[] = [];
    for (const raw of input.split(",")) {
        const tag = raw.replace(/\s+/g, " ").trim();
        if (tag && !tags.some((known) => known.toLowerCase() === tag.toLowerCase())) tags.push(tag);
    }
    return tags;
}

export type RepertoireStats = {
    mostSung: { song: ChoirSong; count: number }[];
    longUnsung: { song: ChoirSong; lastSung: string | null }[];
};

/** Most sung in the last twelve months, and songs not sung for over a year. Archived songs are left out. */
export function repertoireStats(songs: ChoirSong[], today: string, limit = 10): RepertoireStats {
    const active = songs.filter((song) => !song.archived);
    const mostSung = active
        .map((song) => ({ song, count: usageOf(song, today).lastYearCount }))
        .filter((entry) => entry.count > 0)
        .sort((a, b) => b.count - a.count || a.song.name.toString().localeCompare(b.song.name.toString(), "de"))
        .slice(0, limit);
    const longUnsung = active
        .filter((song) => isLongUnsung(song, today))
        .map((song) => ({ song, lastSung: usageOf(song, today).lastSung }))
        // Longest ago first; never sung at the end.
        .sort((a, b) => (a.lastSung ?? "9999").localeCompare(b.lastSung ?? "9999")
            || a.song.name.toString().localeCompare(b.song.name.toString(), "de"));
    return { mostSung, longUnsung };
}

export type ExportRow = { date: string; song: string; occasion: string; source: string };

function csvCell(value: string): string {
    // Cells starting with = + - @ would be run as formulas by spreadsheet programs.
    const safe = /^[=+\-@]/.test(value) ? `'${value}` : value;
    return /[";\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** Semicolon-separated CSV with a BOM, as German Excel expects it. */
export function performancesCsv(rows: ExportRow[], header: [string, string, string, string]): string {
    const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date) || a.song.localeCompare(b.song, "de"));
    const lines = [header, ...sorted.map((row) => [row.date, row.song, row.occasion, row.source])];
    return "﻿" + lines.map((line) => line.map(csvCell).join(";")).join("\r\n") + "\r\n";
}
